import { faSearch } from '@fortawesome/pro-light-svg-icons';
import { faFileArrowUp } from '@fortawesome/pro-light-svg-icons/faFileArrowUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Client from '@spike/client-model';
import { clearClientsThunk, ClientsStatus, fetchClientsThunk, searchClientsThunk } from '@spike/clients-action';
import Analytics from 'analytics/client';
import { Events } from 'analytics/events';
import { showErrorThunk } from '@spike/notifications-action';
import { clearPetsThunk } from '@spike/pets-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import ApiClient from 'api';
import clsx from 'clsx';
import OnboardingTutorial from 'components/OnboardingTutorial';
import { Button, ToolbarButton, ToolbarItem } from 'components/UI';
import CreateBookingDrawer from 'components/UI/CreateBookingDrawer';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { useApiClientWrapper, useMarketplace, usePermissions } from 'hooks';
import { useDebounce } from 'hooks/useDebounce';
import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { reduceResolution, wbp } from 'Theme';
import ClientBulkUpload from './ClientBulkUpload';
import ClientsNotCreated from './ClientsNotCreated';
import ClientsNotFound from './ClientsNotFound';
import Header from './Header';
import Table from './Table';
import { refreshMarketplaceThunk } from '@spike/marketplace-action';
import { SETTINGS_TRUE_VALUE } from '@spike/marketplace-model';
import { clientSlides } from './Slides';
import ClientViewFilter, { ClientView } from './ClientViewFilter';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/Spinner';

interface ClientProps {
    onAddClient?: () => void;
    onEditClient?: (clientId: number) => void;
    onEditPet?: (petid: number, clientId: number) => void;
}

type ClientOrder = 'name_asc' | 'name_desc' | 'last_visit_asc' | 'last_visit_desc';

interface BookingNew {
    clientId: number;
    petId: number;
    clientName: string;
}

const backendFieldSeparator = ';';

export const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            backgroundColor: 'white'
        },
        wrapper: {
            height: '100%',
            width: '100%'
        },
        onboardingContainer: {
            height: '100%'
        },
        bulkUploadContainerWrapper: {
            position: 'absolute',
            top: 0
        },
        bulkUploadContainer: {
            top: 0,
            left: 0,
            zIndex: 9999,
            width: '100%',
            height: '100%',
            position: 'fixed',
            backgroundColor: 'white'
        },
        overlayScroll: {
            height: '100%',
            width: '100%',
            [theme.breakpoints.only('lg')]: {
                width: '99,5%'
            }
        },
        table: {
            width: '100%'
        },
        rightActionsRow: {
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 12
        },
        viewFilterWrapper: {
            display: 'flex',
            flexShrink: 0,
            minWidth: 180,
            justifyContent: 'flex-end'
        },
        buttonHeaderHide: {
            justifyContent: 'flex-start',
            alignItems: 'center',
            [theme.breakpoints.down(wbp)]: {
                paddingLeft: `${reduceResolution(25)}px`,
                paddingRight: `${reduceResolution(24)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingLeft: '25px',
                paddingRight: '24px'
            }
        },
        clientsNotCreatedContainer: {
            [theme.breakpoints.down('sm')]: {
                marginTop: '27px'
            },
            [theme.breakpoints.only('md')]: {
                marginTop: '43px'
            },
            [theme.breakpoints.only('lg')]: {
                marginTop: '57px'
            },
            [theme.breakpoints.up('xl')]: {
                marginTop: '87px'
            }
        },
        input: {
            'borderRadius': 30,
            'background': '#FFFFFF !important',

            '& input': {
                '&::placeholder': {
                    opacity: 1,
                    color: '#7A7A7A'
                },

                [theme.breakpoints.down('sm')]: {
                    height: 47,
                    paddingTop: 0,
                    paddingBottom: 0,
                    fontSize: 14
                }
            },
            '& .MuiInputAdornment-root': {
                'opacity': 0.3,
                'marginRight': 0,
                'pointer-events': 'none',

                '& svg': {
                    cursor: 'default'
                },
                [theme.breakpoints.down('md')]: {
                    'opacity': 1,
                    '& svg': {
                        color: '#000',
                        fontWeight: 400
                    }
                }
            }
        },
        inputContainer: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 11px',
            borderRadius: '12px',
            backgroundColor: '#F1F1F1',
            [theme.breakpoints.up('md')]: {
                paddingLeft: '22px',
                paddingRight: '22px',
                paddingBottom: '14px'
            }
        },
        inputBox: {},
        search: {
            [theme.breakpoints.down(wbp)]: {
                marginLeft: `${reduceResolution(15)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                marginLeft: '5px'
            }
        },
        headerBoton: {},
        clickable: {
            cursor: 'pointer'
        },
        iconSearch: {
            [theme.breakpoints.down(wbp)]: {
                paddingLeft: `${reduceResolution(5)}px`,
                height: `${reduceResolution(18)}px`
            },
            [theme.breakpoints.up(wbp)]: {
                paddingLeft: '5px',
                height: '18px'
            }
        },
        containerHeader: {
            paddingTop: 17,

            [theme.breakpoints.up('md')]: {
                paddingTop: '30px',
                backgroundColor: '#FAFAFA'
            }
        },
        containerButtonHeader: {
            display: 'flex',

            [theme.breakpoints.down('sm')]: {
                gap: 7,
                padding: '0px 16px',
                alignItems: 'center'
            },
            [theme.breakpoints.up('md')]: {
                paddingBottom: '18px'
            }
        },
        toolbarCenter: {
            fontSize: 18,
            marginTop: 'auto',
            marginRight: 20,
            alignItems: 'end',
            marginLeft: -20,

            [theme.breakpoints.down('sm')]: {
                display: 'none'
            },
            [theme.breakpoints.up('lg')]: {
                marginBottom: '8px'
            }
        },
        containerSearch: {
            paddingTop: 23,
            paddingLeft: 16,
            paddingRight: 16,
            paddingBottom: 21,

            [theme.breakpoints.up('md')]: {
                paddingTop: '20px',
                paddingLeft: '25px',
                paddingRight: '25px',
                borderRadius: '12px',
                paddingBottom: '18px'
            }
        },
        importMobileButton: {
            'width': 38,
            'height': 38,
            'color': '#fff',
            'flex': '0 0 38px',
            'padding': '0px',
            'minWidth': 'unset',
            'borderRadius': '50%',
            'alignItems': 'center',
            'display': 'inline-flex',
            'justifyContent': 'center',
            'backgroundColor': '#F1F1F1 !important',
            '& svg': {
                width: 16,
                height: 16,
                color: '#000000'
            },
            [theme.breakpoints.up('md')]: {
                display: 'none'
            }
        },
        csv: {
            'textDecoration': 'none',
            [theme.breakpoints.down('sm')]: {
                //width: "45%"
            },
            '& button': {
                [theme.breakpoints.down('sm')]: {
                    'height': 39,
                    'width': 39,
                    'alignItems': 'center',
                    'display': 'flex',
                    'justifyContent': 'center',

                    'marginLeft': 10,
                    '& svg': {
                        width: 16,
                        height: 16
                    }
                }
            }
        }
    })
);

const Clients: FunctionComponent<ClientProps> = props => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const apiClientWrapepr = useApiClientWrapper();
    const marketplace = useMarketplace();
    const permissions = usePermissions();
    const location = useLocation();

    const csvLinkRef = useRef<any>(null);

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const clientsStatus = useSelector<RootState, ClientsStatus>(state => state.clients.status);
    const clients = useSelector<RootState, Array<Client>>(state => state.clients.list);
    const page = useSelector<RootState, number>(state => Number(state.clients.page));
    const pageSize = useSelector<RootState, number>(state => Number(state.clients.size));
    const totalRecords = useSelector<RootState, number>(state =>
        state.clients.total_records ? state.clients.total_records : 0
    );
    const clientsDone = useSelector<RootState, boolean>(state => state.onboardingSteps.onboardingSteps.clientsDone);
    const marketplaceId = useSelector<RootState, number | undefined>(
        state => state.login.auth.marketplaceId || state.marketplace.marketplace?.id
    );

    const totalPages = useSelector<RootState, number | undefined>(state => state.clients.total_pages);

    const onboardingPageParams = {
        title: 'Your Clients',
        buttonLabel: 'Add Client',
        imageWidth: 300,
        imageHeight: 500
    };

    const [loading, setLoading] = useState(true);
    const [isLastSearchAll, setIsLastSearchAll] = useState(true);
    const [searchText, setSearchText] = useState('');
    const debouncedSearchText = useDebounce(searchText, 300);
    const [showNewBooking, setShowNewBooking] = useState<BookingNew | undefined>(undefined);
    const [showSearch, setShowSearch] = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [order, setOrder] = useState<ClientOrder>('name_asc');

    const leadsEnabled = marketplace.settings?.find(s => s.key === 'leads_enabled')?.value === SETTINGS_TRUE_VALUE;
    const [view, setView] = useState<ClientView | undefined>(leadsEnabled ? 'all' : undefined);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('import') === 'true') {
            setShowBulkUpload(true);
        }
    }, [location.search]);

    const [exporting, setExporting] = useState<{
        loading: boolean;
        headers: Array<string>;
        data: Array<Array<string>>;
    }>({ loading: false, headers: [], data: [] });

    const [searchLoading, setSearchLoading] = useState(false);

    const fetchClients = (page: number, pageSize = 20, nextOrder = order, nextView = view) => {
        if (searchText.length === 0) {
            setIsLastSearchAll(true);
            dispatch(fetchClientsThunk(apiClientWrapepr, page, pageSize, true, nextOrder, nextView));
            setSearchLoading(true);
        } else if (searchText.length >= 3) {
            setIsLastSearchAll(false);
            const encoded = encodeURIComponent(searchText);
            dispatch(searchClientsThunk(apiClientWrapepr, encoded, page, pageSize, true, nextView));
            setSearchLoading(true);
        }
    };

    useEffect(() => {
        dispatch(refreshMarketplaceThunk(apiClientWrapepr));
    }, []);

    useEffect(() => {
        setLoading(true);
        dispatch(clearPetsThunk());
        dispatch(clearClientsThunk());
        fetchClients(1, 20);
        return () => {
            dispatch(clearClientsThunk());
            dispatch(clearPetsThunk());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showBulkUpload]);

    useNonInitialEffect(() => {
        switch (clientsStatus) {
            case ClientsStatus.FetchSuccess:
                setSearchLoading(false);
                setLoading(false);
                break;
            case ClientsStatus.Error:
                setSearchLoading(false);
                setLoading(false);
        }
    }, [clientsStatus]);

    useNonInitialEffect(() => {
        fetchClients(page, pageSize);
    }, [debouncedSearchText]);

    useNonInitialEffect(() => {
        if (!exporting.loading && exporting.data.length > 0) {
            csvLinkRef.current?.export();
        }
    }, [exporting.data]);

    const changePageHandler = (page: number) => {
        fetchClients(page, pageSize);
    };

    const changePageSizeHandler = (pageSize: number) => {
        fetchClients(page, pageSize);
    };

    const changeOrderHandler = (nextOrder: ClientOrder) => {
        setOrder(nextOrder);
        fetchClients(1, pageSize, nextOrder);
    };

    const changeViewHandler = (nextView: ClientView) => {
        setView(nextView);
        fetchClients(1, pageSize, order, nextView);
    };

    const backNewBookingHandler = () => {
        setShowNewBooking(undefined);
    };

    const saveNewBookingHandler = () => {
        setShowNewBooking(undefined);
    };

    const handleClickSearch = (): void => {
        setSearchText('');
        setShowSearch(!showSearch);
    };

    const bookHandler = (client: Client) => {
        const pet = client.pets.filter(pet => pet.active && !pet.deceased)[0];
        pet &&
            setShowNewBooking({
                clientId: client.id ?? 0,
                clientName: `${client.firstName} ${client.lastName}`,
                petId: pet.id
            });
    };

    const exportHandler = () => {
        setExporting(prev => ({
            ...prev,
            loading: true,
            headers: [],
            data: []
        }));

        ApiClient.get<Array<string>>(
            `/export_data?marketplace_id=${apiClientWrapepr.marketplaceId}`,
            apiClientWrapepr.requestConfig
        )
            .then(response => {
                const headers: Array<string> = response.data[0].split(backendFieldSeparator);

                const data: Array<Array<string>> = response.data.slice(1).map(row => row.split(backendFieldSeparator));

                setExporting(prev => ({
                    ...prev,
                    loading: false,
                    headers: [...headers],
                    data: [...data]
                }));
            })
            .catch(() => {
                dispatch(showErrorThunk('Error exporting.'));
                setExporting(prev => ({ ...prev, loading: false }));
            });
    };

    const searchIcon = (
        <FontAwesomeIcon
            onClick={handleClickSearch}
            icon={faSearch}
            className={clsx(!showSearch ? classes.iconSearch : classes.search)}
        />
    );

    // Unified one-shot Add Client tracking wrapper used across all CTA entry points (tutorial, empty states, header button)
    const handleAddClientTracked = () => {
        if (!clientsDone) {
            try {
                const key = `event_one_shot_${Events.ADD_CLIENT_CLICKED}_${marketplaceId}`;
                if (!marketplaceId) {
                    // Fire even if id unavailable (will not persist one-shot key)
                    Analytics.track(Events.ADD_CLIENT_CLICKED as any, {});
                } else if (localStorage.getItem(key) !== '1') {
                    Analytics.track(Events.ADD_CLIENT_CLICKED as any, { marketplace_id: marketplaceId });
                    localStorage.setItem(key, '1');
                }
            } catch (e) {
                // non-blocking
            }
        }
        props.onAddClient && props.onAddClient();
    };

    const onBoarding = (
        <Box className={classes.onboardingContainer}>
            <OnboardingTutorial
                id="clients_onboarding"
                titlePage={onboardingPageParams.title}
                addButtonLabel={onboardingPageParams.buttonLabel}
                onPressAdd={handleAddClientTracked}
                imageWidth={onboardingPageParams.imageWidth}
                imageHeight={onboardingPageParams.imageHeight}
                isMobile={isMobile}
                slides={clientSlides}
            />
        </Box>
    );

    const renderExportCSV = () => {
        return (
            <ExportCSV
                headers={exporting.headers}
                records={exporting.data}
                filename={`Client Information ${marketplace.basics.businessName}.csv`}
                ref={csvLinkRef}
                exporting={exporting.loading}
                onClick={exportHandler}
            />
        );
    };

    const header = (
        <Box className={clsx(classes.containerHeader)}>
            <Box className={clsx(classes.containerButtonHeader)}>
                <Header
                    title={`Clients List ${totalRecords === 0 ? '' : '(' + totalRecords + ')'}`}
                    buttonLabel={isMobile ? 'Add' : 'Add Client'}
                    onClick={handleAddClientTracked}
                    id="clients_list_add_button"
                />
                <Button
                    className={classes.importMobileButton}
                    label={<FontAwesomeIcon icon={faFileArrowUp} />}
                    onClick={() => setShowBulkUpload(true)}
                    loading={exporting.loading}
                />
                <ToolbarButton className={classes.toolbarCenter}>
                    <ToolbarItem
                        id="import_clients"
                        text="Import Clients"
                        icon={faFileArrowUp}
                        onClick={() => setShowBulkUpload(true)}
                    />
                </ToolbarButton>
            </Box>
            {!showSearch && (
                <Box className={clsx(classes.containerSearch)}>
                    <SearchBar
                        id="clients_search_by_client_or_pet_input"
                        placeholder="Search by client name, pet name, phone number or email"
                        className={classes.input}
                        searchText={searchText}
                        onChangeSearchText={setSearchText}
                        rightActions={
                            <Box className={classes.rightActionsRow}>
                                {leadsEnabled && view && (
                                    <Box className={classes.viewFilterWrapper}>
                                        <ClientViewFilter
                                            value={view}
                                            onChange={changeViewHandler}
                                        />
                                    </Box>
                                )}
                                {permissions.clients.export ? renderExportCSV() : null}
                            </Box>
                        }
                    />
                </Box>
            )}
        </Box>
    );

    const table = (
        <Table
            page={clients}
            totalRecords={totalRecords}
            pageNumber={page}
            pageSize={pageSize}
            loading={loading || searchLoading}
            onChangePage={changePageHandler}
            onChangePageSize={changePageSizeHandler}
            onChangeOrder={changeOrderHandler}
            className={classes.table}
            onEditClient={props.onEditClient}
            onEditPet={props.onEditPet}
            onBook={bookHandler}
            hasNextPage={!!totalPages && page < totalPages}
        />
    );

    const newBookingView = (
        <CreateBookingDrawer
            parentID={showNewBooking?.clientId}
            parentName={showNewBooking?.clientName}
            onClose={backNewBookingHandler}
            onBooked={saveNewBookingHandler}
            showTabs={false}
        />
    );

    const bulkUploadView = (
        <Box className={classes.bulkUploadContainerWrapper}>
            <Box className={classes.bulkUploadContainer}>
                <ClientBulkUpload
                    onClose={() => {
                        setLoading(true); // Set loading immediately to prevent flash of client list
                        setShowBulkUpload(false);
                    }}
                />
            </Box>
        </Box>
    );

    const clientsNotFound = (
        <Box className={classes.clientsNotCreatedContainer}>
            <ClientsNotFound
                disableAddButton={false}
                onAddClient={props.onAddClient}
            />
        </Box>
    );

    const clientsDontExist = (
        <Box className={classes.clientsNotCreatedContainer}>
            <ClientsNotCreated
                disableAddButton={false}
                onAddClient={props.onAddClient}
            />
        </Box>
    );

    const loadingBlock = (
        <Box style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <Spinner />
        </Box>
    );

    return (
        <Box className={classes.container}>
            {!loading && !clientsDone && clients.length === 0 ? (
                onBoarding
            ) : (
                <Box className={classes.wrapper}>
                    <Box>{(!loading || clients.length > 0) && header}</Box>
                    <Box className={classes.wrapper}>
                        {/*
                        {(!loading || clients.length > 0) && table}
                        {!loading && clients.length === 0 && !isLastSearchAll && clientsNotFound}
                        {!loading && clients.length === 0 && isLastSearchAll && clientsDontExist}
                        */}

                        <>
                            {loading && loadingBlock}
                            {!loading && table}
                            {!loading && !searchLoading && clients.length === 0 && !isLastSearchAll && clientsNotFound}
                            {!loading && !searchLoading && clients.length === 0 && isLastSearchAll && clientsDontExist}
                        </>
                    </Box>
                </Box>
            )}
            {showNewBooking && newBookingView}
            {showBulkUpload && bulkUploadView}
        </Box>
    );
};

export default Clients;
